/**
 * Currency conversion utilities for frontend
 */

import { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

/**
 * Detect user's currency based on their location
 */
export const detectUserCurrency = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/currency/detect`);
    
    if (response.ok) {
      const data = await response.json();
      return {
        currency: data.currency,
        country: data.country,
        symbol: data.symbol,
        success: true
      };
    }
  } catch (error) {
    console.error('Currency detection failed:', error);
  }
  
  // Fallback
  return {
    currency: 'USD',
    country: 'US',
    symbol: '$',
    success: false
  };
};

/**
 * Get subscription prices with currency conversion
 */
export const getSubscriptionPricesWithCurrency = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/currency/subscription-prices`);
    
    if (response.ok) {
      const data = await response.json();
      return {
        prices: data.prices,
        success: true
      };
    }
  } catch (error) {
    console.error('Currency subscription prices failed:', error);
  }
  
  // Fallback to USD prices
  return {
    prices: {
      'starter_monthly': {
        'amount': 19.99,
        'currency': 'USD',
        'formatted': '$19.99',
        'original_usd': 19.99,
        'country': 'US'
      },
      'basic_monthly': {
        'amount': 29.99,
        'currency': 'USD',
        'formatted': '$29.99',
        'original_usd': 29.99,
        'country': 'US'
      },
      'premium_monthly': {
        'amount': 49.99,
        'currency': 'USD',
        'formatted': '$49.99',
        'original_usd': 49.99,
        'country': 'US'
      },
      'enterprise_monthly': {
        'amount': 99.99,
        'currency': 'USD',
        'formatted': '$99.99',
        'original_usd': 99.99,
        'country': 'US'
      }
    },
    success: false
  };
};

/**
 * Convert amount between currencies
 */
export const convertCurrency = async (amount, fromCurrency = 'USD', toCurrency = null) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/currency/convert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount,
        from_currency: fromCurrency,
        to_currency: toCurrency
      }),
    });
    
    if (response.ok) {
      const data = await response.json();
      return {
        ...data,
        success: true
      };
    }
  } catch (error) {
    console.error('Currency conversion failed:', error);
  }
  
  // Fallback
  return {
    original_amount: amount,
    original_currency: fromCurrency,
    converted_amount: amount,
    converted_currency: fromCurrency,
    formatted: `${fromCurrency === 'USD' ? '$' : ''}${amount.toFixed(2)}`,
    success: false
  };
};

/**
 * Format price with currency symbol
 */
export const formatPrice = (amount, currency = 'USD') => {
  const symbols = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CNY': '¥',
    'KRW': '₩',
    'INR': '₹',
    'CAD': 'C$',
    'AUD': 'A$',
    'CHF': 'CHF',
    'BRL': 'R$',
    'MXN': '$',
    'SGD': 'S$',
    'NZD': 'NZ$',
    'NOK': 'kr',
    'SEK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'CZK': 'Kč',
    'HUF': 'Ft',
    'RUB': '₽',
    'TRY': '₺',
    'ZAR': 'R',
    'THB': '฿',
    'AED': 'د.إ',
    'SAR': '﷼',
    'ILS': '₪'
  };
  
  const symbol = symbols[currency] || currency;
  return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Get currency symbol
 */
export const getCurrencySymbol = (currency = 'USD') => {
  const symbols = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CNY': '¥',
    'KRW': '₩',
    'INR': '₹',
    'CAD': 'C$',
    'AUD': 'A$',
    'CHF': 'CHF',
    'BRL': 'R$',
    'MXN': '$',
    'SGD': 'S$',
    'NZD': 'NZ$',
    'NOK': 'kr',
    'SEK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'CZK': 'Kč',
    'HUF': 'Ft',
    'RUB': '₽',
    'TRY': '₺',
    'ZAR': 'R',
    'THB': '฿',
    'AED': 'د.إ',
    'SAR': '﷼',
    'ILS': '₪'
  };
  
  return symbols[currency] || currency;
};

/**
 * Hook for using currency detection in React components
 */
export const useCurrencyDetection = () => {
  const [userCurrency, setUserCurrency] = useState({
    currency: 'USD',
    country: 'US', 
    symbol: '$',
    loading: true
  });
  
  useEffect(() => {
    const detectCurrency = async () => {
      const result = await detectUserCurrency();
      setUserCurrency({
        ...result,
        loading: false
      });
    };
    
    detectCurrency();
  }, []);
  
  return userCurrency;
};

export default {
  detectUserCurrency,
  getSubscriptionPricesWithCurrency,
  convertCurrency,
  formatPrice,
  getCurrencySymbol,
  useCurrencyDetection
};